import { useAssignInfo } from "@saleor/accountSettings/misc";
import { useAccountAssignMall } from "@saleor/accountSettings/queries";
import DropOffActiveMalls from "@saleor/dropOff/components/DropOffActiveMalls";
import DropOffTabs from "@saleor/dropOff/components/DropOffTabs/DropOffTabs";
import { useMallListDropOffQuery } from "@saleor/dropOff/queries";
import { MallListDropOff_malls_edges_node } from "@saleor/dropOff/types/MallListDropOff";
import { DropOffListUrlQueryParams, dropOffUrl } from "@saleor/dropOff/url";
import { getFilterVariables } from "@saleor/dropOff/utils/filters";
import useListSettings from "@saleor/hooks/useListSettings";
import useNavigator from "@saleor/hooks/useNavigator";
import usePaginator, {
  createPaginationState
} from "@saleor/hooks/usePaginator";
import useUser from "@saleor/hooks/useUser";
import { maybe } from "@saleor/misc";
import { ListViews } from "@saleor/types";
import { SmopUserTypeCode } from "@saleor/types/globalTypes";
import { canManageDropOff } from "@saleor/utils/handlers/userHandler";
import { getQueryParamString } from "@saleor/utils/urls";
import { parse as parseQs } from "qs";
import * as React from "react";

interface DropOffListProps {
  params: DropOffListUrlQueryParams;
}

const DropOffList: React.FC<DropOffListProps> = ({ params }) => {
  const { user } = useUser();
  const userInfo = useAssignInfo(user);
  const navigate = useNavigator();
  const paginate = usePaginator();

  const disableAction = !canManageDropOff();

  const { mall } = parseQs(location.search.substring(1));
  const { updateListSettings, settings } = useListSettings(
    ListViews.DISPATCH_LIST
  );
  const paginationState = createPaginationState(settings.rowNumber, params);

  const { data: accountMallAssignmentData } = useAccountAssignMall({
    displayLoader: true,
    skip: !userInfo?.mall,
    variables: {
      id: userInfo?.mall
    }
  });

  const queryVariables = React.useMemo(
    () => ({
      ...paginationState,
      filter: getFilterVariables({
        params
      })
    }),
    [params, settings.rowNumber]
  );

  const getFilterMallId = (): string[] => {
    if (user?.accessGroup?.smopUsertypeCode === SmopUserTypeCode.OMI_OPS) {
      const id = accountMallAssignmentData?.mall?.id;
      return id ? [id] : [];
    }
    const mallStr = getQueryParamString(mall);
    return mallStr ? [mallStr] : [];
  };

  const { data: mallList, loading: mallListLoading } = useMallListDropOffQuery({
    displayLoader: true,
    variables: {
      ...queryVariables,
      filter: {
        id: getFilterMallId()
      }
    }
  });

  const { loadNextPage, loadPreviousPage, pageInfo } = paginate(
    maybe(() => mallList.malls.pageInfo),
    paginationState,
    params
  );

  const getDropOffList = () => {
    if (
      user?.accessGroup?.smopUsertypeCode &&
      user?.accessGroup?.smopUsertypeCode === SmopUserTypeCode.OMI_OPS
    ) {
      return mallList?.malls?.edges.filter(e => {
        if (e?.node?.id === accountMallAssignmentData?.mall?.id) {
          return e.node;
        }
      });
    } else {
      if (mall) {
        return mallList?.malls?.edges.filter(e => {
          if (e?.node?.id === mall) {
            return e.node;
          }
        });
      }
      return mallList?.malls?.edges.map(e => e);
    }
  };

  const mallListData: MallListDropOff_malls_edges_node[] = getDropOffList()?.map(
    e => e.node
  );

  if (user.userType.code !== SmopUserTypeCode.OMI_OPS && !mall) {
    return (
      <DropOffActiveMalls
        dropOffList={mallListData}
        settings={settings}
        disabled={mallListLoading}
        pageInfo={pageInfo}
        onNextPage={loadNextPage}
        onPreviousPage={loadPreviousPage}
        onUpdateListSettings={updateListSettings}
        onRowClick={id => () => navigate(dropOffUrl(id))}
      />
    );
  } else {
    return (
      <DropOffTabs
        mallData={mallListData}
        isOps={user.userType.code === SmopUserTypeCode.OMI_OPS}
        disableAction={disableAction}
      />
    );
  }
};

export default DropOffList;
